'use client'

import * as React from 'react'
import { useRouter, notFound, usePathname } from 'next/navigation'
import Image from 'next/image'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { lazyComponent } from 'utils/lazy-component'
import { useLocale, useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import debounce from 'lodash.debounce'
import { defaultLanguage, mapLocaleToContentLanguage } from 'cf-i18n/constants'
import { ContentLanguage } from 'codegen/types'
import { unArray } from 'utils/array'
import { ChevronRightIcon, WandIcon } from 'primitives/icons'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { Breadcrumbs } from '@repo/web-ui/components/breadcrumbs'
import { Select } from '@repo/web-ui/components/select/select'
import { CloseIcon } from 'primitives/icons/close'
import { LFS_URL } from '@repo/web-utils/env'
import { getPathnameWithoutLocale } from 'cf-i18n/helpers'

import { FontGeneratorCategories } from './font-generator-widget/font-generator-widget.types'
import { LinkModule } from './link-module/link-module'
import { mapFromGQLContentLinkBlockToLinkSectionProps } from './link-module/link-module.utils'
import { SharePage } from './share-page/share-page'
import { useLeftRightAnimation } from './use-left-right-animation/use-left-right-animation'
import * as Slider from './slider/slider'
import { useContentLinkBlockQuery } from '@/gen/content'
import type { ContentLinkBlock } from '@repo/graphql-types'
import { mapSpecialCharacters } from '@/utils/string'

const FontGeneratorWidgetComponent = lazyComponent(() =>
  import('./font-generator-widget/font-generator-widget').then(
    module => module.FontGeneratorWidget,
  ),
)
const TranslatorWidgetComponent = lazyComponent(() =>
  import('./translator-widget/translator-widget').then(module => module.TranslatorWidget),
)

export const SURPRISE_ME_TEXT = 'The quick brown fox jumps over the lazy dog'

const howToImages = [
  `${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Step01.png`,
  `${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Step02.png`,
  `${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Step03.png`,
]

const routeToCategoryIdMap = new Map([
  [AppRoutes.TOOLS_FANCY_FONT_GENERATOR, FontGeneratorCategories.FONT_GENERATOR],
  [AppRoutes.TOOLS_FONT_GENERATOR, FontGeneratorCategories.FONT_GENERATOR],
  [AppRoutes.TOOLS_CURSIVE_FONT_GENERATOR, FontGeneratorCategories.CURSIVE],
  [AppRoutes.TOOLS_FACEBOOK_FONTS_GENERATOR, FontGeneratorCategories.FACEBOOK],
  [AppRoutes.TOOLS_GREEK_TEXT_GENERATOR, FontGeneratorCategories.GREEK],
  [AppRoutes.TOOLS_GLITCH_TEXT_GENERATOR, FontGeneratorCategories.GLITCH],
  [AppRoutes.TOOLS_ITALICS_TEXT_GENERATOR, FontGeneratorCategories.ITALICS],
  [AppRoutes.TOOLS_BIG_TEXT_GENERATOR, FontGeneratorCategories.BIG],
  [AppRoutes.TOOLS_CALLIGRAPHY_FONT_GENERATOR, FontGeneratorCategories.CALLIGRAPHY],
  [AppRoutes.TOOLS_SMALL_TEXT_GENERATOR, FontGeneratorCategories.SMALL],
  [AppRoutes.TOOLS_WINGDINGS_TRANSLATOR, FontGeneratorCategories.WINGDINGS],
  [AppRoutes.TOOLS_STYLISH_TEXT_GENERATOR, FontGeneratorCategories.STYLE],
  [AppRoutes.TOOLS_ASCII_ART_GENERATOR, FontGeneratorCategories.ASCII],
  [AppRoutes.TOOLS_CURSED_TEXT_GENERATOR, FontGeneratorCategories.CURSED],
  [AppRoutes.TOOLS_UPSIDE_DOWN_TEXT_GENERATOR, FontGeneratorCategories.UPSIDE],
  [AppRoutes.TOOLS_TYPEWRITER_FONT_GENERATOR, FontGeneratorCategories.TYPEWRITER],
  [AppRoutes.TOOLS_UNDERLINE_TEXT_GENERATOR, FontGeneratorCategories.UNDERLINE],
  [AppRoutes.TOOLS_BOLD_TEXT_GENERATOR, FontGeneratorCategories.BOLD],
  [AppRoutes.TOOLS_GOTHIC_FONT_GENERATOR, FontGeneratorCategories.GOTHIC],
  [AppRoutes.TOOLS_SPECIAL_TEXT_GENERATOR, FontGeneratorCategories.SPECIAL],
  [AppRoutes.TOOLS_TIKTOK_FONT_GENERATOR, FontGeneratorCategories.TIKTOK],
  [AppRoutes.TOOLS_ZALGO_TEXT_GENERATOR, FontGeneratorCategories.ZALGO],
  [AppRoutes.TOOLS_AESTHETIC_FONTS_GENERATOR, FontGeneratorCategories.AESTHETIC],
  [AppRoutes.TOOLS_VAPORWAVE_TEXT_GENERATOR, FontGeneratorCategories.VAPORWARE],
  [AppRoutes.TOOLS_SCARY_TEXT_GENERATOR, FontGeneratorCategories.SCARY],
  [AppRoutes.TOOLS_INSTAGRAM_FONTS_GENERATOR, FontGeneratorCategories.INSTAGRAM],
  [AppRoutes.TOOLS_TWITTER_FONTS_GENERATOR, FontGeneratorCategories.TWITTER],
  [AppRoutes.TOOLS_MIRROR_TEXT_GENERATOR, FontGeneratorCategories.MIRROR],
  [AppRoutes.TOOLS_NUMBER_FONT_GENERATOR, FontGeneratorCategories.NUMBER],
  [AppRoutes.TOOLS_COOL_FONTS_GENERATOR, FontGeneratorCategories.COOL],
  [AppRoutes.TOOLS_DISCORD_FONTS_GENERATOR, FontGeneratorCategories.DISCORD],
  [AppRoutes.TOOLS_HACKED_TEXT_GENERATOR, FontGeneratorCategories.HACKED],
  [AppRoutes.TOOLS_WIDE_TEXT_GENERATOR, FontGeneratorCategories.WIDE],
  [AppRoutes.TOOLS_CREEPY_TEXT_GENERATOR, FontGeneratorCategories.CREEPY],
  [AppRoutes.TOOLS_STRIKE_THROUGH_TEXT_GENERATOR, FontGeneratorCategories.CROSS],
  [AppRoutes.TOOLS_BUBBLE_WORDS_GENERATOR, FontGeneratorCategories.BUBBLE],
  [AppRoutes.TOOLS_BLOCK_FONT_GENERATOR, FontGeneratorCategories.BLOCK],
  [AppRoutes.TOOLS_FUTURISTIC_FONT_GENERATOR, FontGeneratorCategories.FUTURISTIC],
  [AppRoutes.TOOLS_WEBDINGS_TRANSLATOR, FontGeneratorCategories.WEBDINGS],
  [AppRoutes.TOOLS_CURVE_TEXT_GENERATOR, FontGeneratorCategories.CURVE],
])

export const FontGeneratorLinksBlockId =
  // oxlint-disable-next-line no-negated-condition
  process.env.NEXT_PUBLIC_ENV !== 'production'
    ? '2UTEDw4W5FILIFprXyQfUelCmH8'
    : '2Ueiiby68BnahoOX1mHCFqtAJWc'
export const ToolsLinksBlockId =
  // oxlint-disable-next-line no-negated-condition
  process.env.NEXT_PUBLIC_ENV !== 'production'
    ? '2URokmdMccSWcBnZBxQukD3p50s'
    : '2UehUD6zbSTLTawaSEfLge8h5Do'
export const FontsLinksBlockId =
  // oxlint-disable-next-line no-negated-condition
  process.env.NEXT_PUBLIC_ENV !== 'production'
    ? '2UeQq2uFv2qIu4gz8ebulmHPAyS'
    : '2UehlHIdC3TF6nu9zh9685TAhbi'

const translatorPages = new Set([
  AppRoutes.TOOLS_WINGDINGS_TRANSLATOR,
  AppRoutes.TOOLS_WEBDINGS_TRANSLATOR,
])

const translatorFontMap = new Map<string, string[]>([
  [AppRoutes.TOOLS_WINGDINGS_TRANSLATOR, ['', `'Wingdings'`, `'Wingdings 2'`, `'Wingdings 3'`]],
  [AppRoutes.TOOLS_WEBDINGS_TRANSLATOR, ['', `'Webdings'`]],
])

const linkClasses = 'text-blue-500 no-underline hover:text-secondary-500'

const generatorSelectConfig = [
  { value: 'font-generator', href: AppRoutes.TOOLS_FONT_GENERATOR },
  { value: 'glitch-text-generator', href: AppRoutes.TOOLS_GLITCH_TEXT_GENERATOR },
  { value: 'small-text-generator', href: AppRoutes.TOOLS_SMALL_TEXT_GENERATOR },
  { value: 'cursed-text-generator', href: AppRoutes.TOOLS_CURSED_TEXT_GENERATOR },
  { value: 'bold-text-generator', href: AppRoutes.TOOLS_BOLD_TEXT_GENERATOR },
  { value: 'zalgo-text-generator', href: AppRoutes.TOOLS_ZALGO_TEXT_GENERATOR },
  { value: 'instagram-fonts-generator', href: AppRoutes.TOOLS_INSTAGRAM_FONTS_GENERATOR },
  { value: 'cool-fonts-generator', href: AppRoutes.TOOLS_COOL_FONTS_GENERATOR },
  { value: 'creepy-text-generator', href: AppRoutes.TOOLS_CREEPY_TEXT_GENERATOR },
  { value: 'aesthetic-fonts-generator', href: AppRoutes.TOOLS_AESTHETIC_FONTS_GENERATOR },
  { value: 'italics-generator', href: AppRoutes.TOOLS_ITALICS_TEXT_GENERATOR },
  { value: 'twitter-fonts-generator', href: AppRoutes.TOOLS_TWITTER_FONTS_GENERATOR },
]

const generatorLinkHrefs = [
  AppRoutes.TOOLS_FONT_GENERATOR,
  AppRoutes.TOOLS_CURSIVE_FONT_GENERATOR,
  AppRoutes.TOOLS_FACEBOOK_FONTS_GENERATOR,
  AppRoutes.TOOLS_GREEK_TEXT_GENERATOR,
  AppRoutes.TOOLS_GLITCH_TEXT_GENERATOR,
  AppRoutes.TOOLS_ITALICS_TEXT_GENERATOR,
  AppRoutes.TOOLS_BIG_TEXT_GENERATOR,
  AppRoutes.TOOLS_CALLIGRAPHY_FONT_GENERATOR,
  AppRoutes.TOOLS_SMALL_TEXT_GENERATOR,
  AppRoutes.TOOLS_WINGDINGS_TRANSLATOR,
  AppRoutes.TOOLS_STYLISH_TEXT_GENERATOR,
  AppRoutes.TOOLS_ASCII_ART_GENERATOR,
  AppRoutes.TOOLS_CURSED_TEXT_GENERATOR,
  AppRoutes.TOOLS_UPSIDE_DOWN_TEXT_GENERATOR,
  AppRoutes.TOOLS_TYPEWRITER_FONT_GENERATOR,
  AppRoutes.TOOLS_UNDERLINE_TEXT_GENERATOR,
  AppRoutes.TOOLS_BOLD_TEXT_GENERATOR,
  AppRoutes.TOOLS_GOTHIC_FONT_GENERATOR,
  AppRoutes.TOOLS_SPECIAL_TEXT_GENERATOR,
  AppRoutes.TOOLS_TIKTOK_FONT_GENERATOR,
  AppRoutes.TOOLS_ZALGO_TEXT_GENERATOR,
  AppRoutes.TOOLS_AESTHETIC_FONTS_GENERATOR,
  AppRoutes.TOOLS_VAPORWAVE_TEXT_GENERATOR,
  AppRoutes.TOOLS_SCARY_TEXT_GENERATOR,
  AppRoutes.TOOLS_INSTAGRAM_FONTS_GENERATOR,
  AppRoutes.TOOLS_TWITTER_FONTS_GENERATOR,
  AppRoutes.TOOLS_MIRROR_TEXT_GENERATOR,
  AppRoutes.TOOLS_NUMBER_FONT_GENERATOR,
  AppRoutes.TOOLS_COOL_FONTS_GENERATOR,
  AppRoutes.TOOLS_DISCORD_FONTS_GENERATOR,
  AppRoutes.TOOLS_HACKED_TEXT_GENERATOR,
  AppRoutes.TOOLS_WIDE_TEXT_GENERATOR,
  AppRoutes.TOOLS_CREEPY_TEXT_GENERATOR,
  AppRoutes.TOOLS_STRIKE_THROUGH_TEXT_GENERATOR,
  AppRoutes.TOOLS_BUBBLE_WORDS_GENERATOR,
  AppRoutes.TOOLS_BLOCK_FONT_GENERATOR,
  AppRoutes.TOOLS_FUTURISTIC_FONT_GENERATOR,
  AppRoutes.TOOLS_WEBDINGS_TRANSLATOR,
  AppRoutes.TOOLS_CURVE_TEXT_GENERATOR,
]

// oxlint-disable-next-line complexity
export function FontGeneratorPageComponent({
  searchParams: initialQuery,
}: {
  searchParams: { [key: string]: string | string[] | undefined }
}) {
  const t = useTranslations('pages.tools.font_generator')
  const t2 = useTranslations()
  const router = useRouter()
  const pathname = getPathnameWithoutLocale(usePathname())
  const ref = React.useRef<HTMLInputElement>(null)

  const locale = useLocale()
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || ContentLanguage.LanguageEn

  const { data: fontGeneratorLinks } = useContentLinkBlockQuery(
    { id: FontGeneratorLinksBlockId, language: contentLanguage },
    { staleTime: Infinity },
  )
  const { data: toolsLinks } = useContentLinkBlockQuery(
    { id: ToolsLinksBlockId, language: contentLanguage },
    { staleTime: Infinity },
  )
  const { data: fontsLinks } = useContentLinkBlockQuery(
    { id: FontsLinksBlockId, language: contentLanguage },
    { staleTime: Infinity },
  )

  React.useEffect(() => {
    ref?.current?.focus()
  }, [])

  const handleUrlUpdate = (value: string) => {
    const newQuery = new URLSearchParams(window.location.search)

    if (value.length === 0) {
      newQuery.delete('text')
    } else {
      newQuery.set('text', value)
    }

    window.history.pushState({}, '', `?${newQuery.toString()}`)
  }

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUrlUpdate = React.useCallback(debounce(handleUrlUpdate, 300), [])

  const generatorSelectItems = generatorSelectConfig.map((item, i) => ({
    ...item,
    label: t(`hero.dropdown.option.${i + 1}`),
  }))

  const isAsciiArt = pathname === AppRoutes.TOOLS_ASCII_ART_GENERATOR

  const initialSelectedItem =
    generatorSelectItems.find(item => item.href === pathname) || generatorSelectItems[0]

  const [selectedItem, setSelectedItem] = React.useState(initialSelectedItem)
  const [filterCategory, setFilterCategory] = React.useState(
    routeToCategoryIdMap.get(pathname) || FontGeneratorCategories.FONT_GENERATOR,
  )
  const [inputValue, setInputValue] = React.useState(unArray(initialQuery.text) || '')
  const [textSize, setTextSize] = React.useState(isAsciiArt ? 12 : 24)

  const generatorLinkItems = generatorLinkHrefs.map((href, i) => ({
    label: t(`faq.section.1.link.${i + 1}`),
    href,
  }))

  const linkItem = generatorLinkItems.find(item => item.href === pathname)

  if (!linkItem) {
    return notFound()
  }

  const breadcrumbs = [
    {
      path: AppRoutes.HOME,
      title: t2('navigation.breadcrumbs.home'),
      className: 'text-cf-white hover:text-cf-white hover:underline',
    },
    {
      path: AppRoutes.TOOLS,
      title: t2('navigation.breadcrumbs.tools'),
      className: 'text-cf-white hover:text-cf-white hover:underline',
    },
    {
      path: '',
      title: linkItem.label || '',
    },
  ]

  const handleSelectChange = (value: string) => {
    const newItem = generatorSelectItems.find(item => item.value === value)

    if (newItem) {
      setSelectedItem(newItem)
      setFilterCategory(
        routeToCategoryIdMap.get(newItem.href) || FontGeneratorCategories.FONT_GENERATOR,
      )
      router.push(`${newItem.href}${window.location.search}`)
    }
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    debouncedUrlUpdate(value)
  }

  const handleTextSize = (value: number) => {
    setTextSize(Math.round(value))
  }

  const h1Text = linkItem.label || ''

  const isTranslatorPage = translatorPages.has(pathname)

  const fontsLinksData = mapFromGQLContentLinkBlockToLinkSectionProps(
    fontGeneratorLinks?.contentLinkBlock as ContentLinkBlock,
  )

  const linkModuleSections = [
    fontsLinksData ? fontsLinksData : null,
    mapFromGQLContentLinkBlockToLinkSectionProps(toolsLinks?.contentLinkBlock as ContentLinkBlock),
    mapFromGQLContentLinkBlockToLinkSectionProps(fontsLinks?.contentLinkBlock as ContentLinkBlock),
  ]

  return (
    <div className="flex flex-col text-[#2a1f3f]">
      <HeroSection
        isTranslatorPage={isTranslatorPage}
        breadcrumbs={breadcrumbs}
        h1Text={h1Text}
        heroSubtitle={t('hero.text', { mainKeyword: h1Text })}
        selectItems={generatorSelectItems}
        selectedValue={selectedItem?.value || 'font-generator'}
        onSelectChange={handleSelectChange}
        inputRef={ref}
        inputPlaceholder={t('hero.input_placeholder')}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        surpriseMeLabel={t('hero.input.button')}
      />
      <section className="flex items-stretch justify-center bg-white">
        <div className="flex w-full max-w-[1200px] flex-col items-center gap-[30px] px-5 py-10">
          <div className="flex w-full flex-col gap-4 md:flex-row md:justify-between">
            <div className="text-16 font-bold">
              {isTranslatorPage
                ? t('results.start_typing')
                : t('results.results_from', {
                    category: h1Text,
                  })}
            </div>
            <div className="flex w-full flex-row items-center justify-center gap-3 md:max-w-[50%] md:justify-end">
              <span className="text-14 flex-shrink-0">{t('results.text_size')}</span>
              <Slider.Root
                orientation="horizontal"
                min={12}
                max={34}
                value={[textSize]}
                defaultValue={[textSize]}
                onValueChange={([value]) => handleTextSize(value)}
                className="-mb-1 h-auto"
              >
                <Slider.Track>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb />
              </Slider.Root>
              <div className="text-14 flex h-[24px] w-[24px] items-center justify-center rounded bg-[#f6f7fb] p-4">
                {textSize}
              </div>
            </div>
          </div>
          <div className="flex w-full">
            {isTranslatorPage ? (
              // oxlint-disable-next-line react/jsx-pascal-case
              <TranslatorWidgetComponent.component
                numberOfBoxes={translatorFontMap.get(linkItem.href)?.length || 2}
                textSize={textSize}
                placeholder={t(
                  `results.${linkItem.href
                    .replace('/tools/', '')
                    .replace('/', '')
                    .replace('-', '_')}.placeholder`,
                )}
                fonts={translatorFontMap.get(linkItem.href!) || []}
              />
            ) : (
              // oxlint-disable-next-line react/jsx-pascal-case
              <FontGeneratorWidgetComponent.component
                fontSize={textSize}
                filterCategory={filterCategory}
                text={mapSpecialCharacters(inputValue || t('hero.input_placeholder'))}
              />
            )}
          </div>
        </div>
      </section>
      <FeatureDetailsSection
        filterCategory={filterCategory}
        showFourthDetail={linkItem.href === AppRoutes.TOOLS_CURVE_TEXT_GENERATOR}
      />
      <HowToSection
        title={t('howTo.title', { mainKeyword: h1Text })}
        steps={([1, 2, 3] as const).map((item, index) => ({
          title: t(`howTo.step${item}.title`),
          image: howToImages[index],
          text: t(`howTo.step${item}.text`, { mainKeyword: h1Text }),
        }))}
      />
      <section className="flex justify-center bg-white">
        <div className="flex w-full max-w-[1400px] flex-col items-center gap-10 px-5 py-10">
          <div className="flex w-full max-w-[900px] flex-col">
            <LinkModule
              title={t('faq.title')}
              subtitle={t('faq.text')}
              sections={linkModuleSections}
            />
          </div>
        </div>
      </section>
      <div className="scroll-align-padding-0 fixed bottom-[0px] right-[0px] z-[5] md:right-[40px] md:flex">
        <SharePage
          inputValue={
            typeof window === 'undefined'
              ? ''
              : `${window.location.origin}${
                  locale === defaultLanguage ? '' : `/${locale}`
                }${pathname}${inputValue.length > 0 ? `?text=${inputValue}` : ''}`
          }
        />
      </div>
    </div>
  )
}

function FeatureDetailsSection({
  filterCategory,
  showFourthDetail,
}: {
  filterCategory: FontGeneratorCategories
  showFourthDetail: boolean
}) {
  const t = useTranslations('pages.tools.font_generator')

  const inView1 = React.useRef<HTMLDivElement>(null)
  const inView2 = React.useRef<HTMLDivElement>(null)
  const inView3 = React.useRef<HTMLDivElement>(null)
  const inView4 = React.useRef<HTMLDivElement>(null)

  const animation1 = useLeftRightAnimation(inView1)
  const animation2 = useLeftRightAnimation(inView2)
  const animation3 = useLeftRightAnimation(inView3)
  const animation4 = useLeftRightAnimation(inView4)

  const richTextTags = {
    br: () => <br />,
    b: (text: React.ReactNode) => <b>{text}</b>,
    fontsLink: (text: React.ReactNode) => (
      <Link className={linkClasses} href={AppRoutes.FONTS}>
        {text}
      </Link>
    ),
    link: (text: React.ReactNode) => (
      <Link className={linkClasses} href={AppRoutes.TOOLS_FONT_GENERATOR}>
        {text}
      </Link>
    ),
  }

  return (
    <LazyMotion features={domAnimation}>
      <section className="flex flex-col items-center bg-white">
        <FeatureDetail
          innerRef={inView1}
          animation={animation1}
          imageSrc={`${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Images_01.png`}
          imageAlt="Font Tool Generator 1"
          title={t(`details.${filterCategory}.1.title`)}
        >
          {t.rich(`details.${filterCategory}.1.text`, richTextTags)}
          <br />
          <br />
          {t.rich('link_to_fonts', {
            link: (text: React.ReactNode) => (
              <Link className={linkClasses} href={AppRoutes.FREEBIES_FONTS}>
                {text}
              </Link>
            ),
          })}
        </FeatureDetail>
        <FeatureDetail
          innerRef={inView2}
          animation={animation2}
          imageSrc={`${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Images_02.png`}
          imageAlt="Font Tool Generator 2"
          title={t(`details.${filterCategory}.2.title`)}
          textFirst={false}
        >
          {t.rich(`details.${filterCategory}.2.text`, richTextTags)}
        </FeatureDetail>
        <FeatureDetail
          innerRef={inView3}
          animation={animation3}
          imageSrc={`${LFS_URL}/web-landing-pages/pages/tools/font-generator/Font-Generator_Images_03.png`}
          imageAlt="Font Tool Generator 3"
          title={t(`details.${filterCategory}.3.title`)}
        >
          {t.rich(`details.${filterCategory}.3.text`, richTextTags)}
        </FeatureDetail>
        {showFourthDetail ? (
          <FeatureDetail
            innerRef={inView4}
            animation={animation4}
            title={t(`details.${filterCategory}.4.title`)}
          >
            {t.rich(`details.${filterCategory}.4.text`, {
              ...richTextTags,
              stylishLink: (text: React.ReactNode) => (
                <Link className={linkClasses} href={AppRoutes.TOOLS_STYLISH_TEXT_GENERATOR}>
                  {text}
                </Link>
              ),
              specialLink: (text: React.ReactNode) => (
                <Link className={linkClasses} href={AppRoutes.TOOLS_SPECIAL_TEXT_GENERATOR}>
                  {text}
                </Link>
              ),
              coolLink: (text: React.ReactNode) => (
                <Link className={linkClasses} href={AppRoutes.TOOLS_COOL_FONTS_GENERATOR}>
                  {text}
                </Link>
              ),
            })}
          </FeatureDetail>
        ) : null}
      </section>
    </LazyMotion>
  )
}

function HeroSection({
  isTranslatorPage,
  breadcrumbs,
  h1Text,
  heroSubtitle,
  selectItems,
  selectedValue,
  onSelectChange,
  inputRef,
  inputPlaceholder,
  inputValue,
  onInputChange,
  surpriseMeLabel,
}: {
  isTranslatorPage: boolean
  breadcrumbs: Array<{ path: string; title: string; className?: string }>
  h1Text: string
  heroSubtitle: string
  selectItems: Array<{ value: string; label: string; href: string }>
  selectedValue: string
  onSelectChange: (value: string) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  inputPlaceholder: string
  inputValue: string
  onInputChange: (value: string) => void
  surpriseMeLabel: string
}) {
  return (
    <section
      className={tw(
        !isTranslatorPage && 'min-h-[300px]',
        'flex justify-center bg-[linear-gradient(to_right,#337DD4,#BC2DB0,#F8597D,#F77309,#FFB400)]',
      )}
    >
      <div className="text-cf-white flex w-full max-w-[1400px] flex-col gap-[30px] px-5 py-10">
        <Breadcrumbs
          className="text-12 md:mb-0"
          items={breadcrumbs}
          separator={<ChevronRightIcon className="size-[15px]" />}
        />
        <div className="flex w-full flex-col items-center gap-[10px]">
          <h1 className="text-30 md:text-36 font-bold leading-9 md:leading-10">{h1Text}</h1>
          <p className="text-14 text-center leading-5">{heroSubtitle}</p>
        </div>
        {isTranslatorPage ? null : (
          <div className="mx-auto flex w-full max-w-[1072px] flex-col items-center gap-[10px] md:flex-row">
            <div className="flex w-full md:max-w-[212px]">
              <Select
                classNameTrigger="w-full text-[#2A1F3F]"
                classNameItem="text-[#2A1F3F] whitespace-nowrap text-12"
                classNameContent="border border-[#e0e2ea] z-[10]"
                items={selectItems}
                value={selectedValue}
                onValueChange={onSelectChange}
                position="popper"
              />
            </div>
            <div className="relative flex w-full items-center">
              <input
                ref={inputRef}
                className={tw(
                  inputValue.length === 0 ? 'pr-[140px]' : 'pr-10',
                  'placeholder-transition border-cf-dreamy-cloud-300 bg-cf-dreamy-cloud-100 h-[40px] w-full rounded-[6px] border px-5 py-[10px] pr-[130px] text-[#2A1F3F] placeholder:text-[#2A1F3F] focus:outline-none',
                )}
                type="text"
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={e => onInputChange(e.target.value)}
              />
              {inputValue.length === 0 ? (
                <button
                  type="button"
                  onClick={() => onInputChange(SURPRISE_ME_TEXT)}
                  className="text-14 absolute inset-y-0 right-0 flex cursor-pointer items-center gap-[5px] pr-3 text-[#8559F9]"
                >
                  <WandIcon className="size-[18px]" />
                  {surpriseMeLabel}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onInputChange('')}
                  className="absolute inset-y-0 right-0 flex cursor-pointer items-center gap-[5px] pr-3 text-[#9093b2]"
                >
                  <CloseIcon className="size-[18px]" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function FeatureDetail({
  innerRef,
  animation,
  imageSrc,
  imageAlt,
  title,
  textFirst = true,
  children,
}: {
  innerRef: React.RefObject<HTMLDivElement | null>
  animation: ReturnType<typeof useLeftRightAnimation>
  imageSrc?: string
  imageAlt?: string
  title: React.ReactNode
  textFirst?: boolean
  children: React.ReactNode
}) {
  const textBlock = (
    <>
      <h2 className="text-24 font-bold leading-8 md:text-[32px]">{title}</h2>
      <p className="text-14 leading-5">{children}</p>
    </>
  )

  const imageBlock = imageSrc ? (
    <Image src={imageSrc} width={525} height={500} alt={imageAlt ?? ''} />
  ) : null

  if (!imageBlock) {
    return (
      <div
        ref={innerRef}
        className="flex w-full max-w-[1400px] flex-col items-center gap-5 px-5 py-10 md:gap-[100px]"
      >
        <div className="flex flex-col items-center gap-5 md:flex-row md:gap-[100px]">
          <m.div
            animate={animation.controlsLeft}
            variants={animation.variants}
            className="order-2 flex max-w-[500px] flex-col gap-5 md:order-1"
          >
            {textBlock}
          </m.div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={innerRef}
      className="flex w-full max-w-[1400px] flex-col items-center gap-5 px-5 py-10 md:gap-[100px]"
    >
      <div className="flex flex-col items-center gap-5 md:flex-row md:gap-[100px]">
        <m.div
          animate={animation.controlsLeft}
          variants={animation.variants}
          className={textFirst ? 'order-2 flex max-w-[500px] flex-col gap-5 md:order-1' : undefined}
        >
          {textFirst ? textBlock : imageBlock}
        </m.div>
        <m.div
          className={textFirst ? 'order-1 md:order-2' : 'flex max-w-[500px] flex-col gap-5'}
          animate={animation.controlsRight}
          variants={animation.variants}
        >
          {textFirst ? imageBlock : textBlock}
        </m.div>
      </div>
    </div>
  )
}

function HowToSection({
  title,
  steps,
}: {
  title: string
  steps: ReadonlyArray<{ title: string; image: string; text: string }>
}) {
  return (
    <section className="flex justify-center bg-[#f6f7fb]">
      <div className="flex w-full max-w-[1400px] flex-col items-center gap-[30px] px-5 py-10">
        <h2 className="text-24 text-center font-bold leading-8 md:text-[32px]">{title}</h2>
        <div className="grid grid-cols-[repeat(1,_minmax(0,_325px))] gap-5 lg:grid-cols-[repeat(3,_minmax(0,_325px))]">
          {steps.map((step, index) => (
            <div key={step.image} className="flex flex-col items-center gap-[10px] p-5">
              <div className="text-20 text-cf-white flex h-[48px] w-[48px] items-center justify-center rounded-[12px] bg-[#8559F9] font-bold">
                {index + 1}
              </div>
              <p className="text-20 text-center font-bold text-[#846EFF]">{step.title}</p>
              <Image
                src={step.image}
                width={285}
                height={182}
                alt={`How to step ${index + 1}`}
                className="rounded-2 mt-[10px]"
              />
              <p className="text-14 text-center leading-5">{step.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
